import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../redux/store';
import {
  formStatus,
  formType,
  NewQuestionArg,
  OptionRemoveArg,
  OptionUpdateArg,
  Question,
  QuestionOrderChangeArg,
  QuestionRemoveArg,
  QuestionUpdateArg,
} from './type';

import {
  addOption,
  addQuestion,
  changeToHigherOrder,
  changeToLowerOrder,
  findQuestion,
  getNewQuestion,
  removeOptionFromSet,
  removeQuestionFromSet,
  updateChildQuestions,
  updateOptionText,
  updateQuestionFields,
} from './formUtils';

export interface FormDataType {
  nameBangla: string;
  nameEnglish: string;
  hash?: string;
  formJson?: string;
  formType?: formType;
  status?: formStatus;
  questions: Question[];
}

const initialState: FormDataType = {
  nameBangla: '',
  nameEnglish: '',
  hash: '',
  formJson: '[]',
  formType: 'DYNAMIC',
  status: 'ACTIVE',
  questions: [],
};

const formSlice = createSlice({
  name: 'formSlice',
  initialState: initialState,
  reducers: {
    addNewQuestion(state, action: PayloadAction<NewQuestionArg>) {
      const { type, isComputed, parentQuestionId, newQuestion } = action.payload;
      let questionToAdd: Question;
      if (!newQuestion) {
        questionToAdd = getNewQuestion(type, isComputed);
      } else {
        questionToAdd = newQuestion;
      }
      const parentQuestion = findQuestion(state.questions, parentQuestionId);
      if (parentQuestion != null) {
        const newQuestionSet = addQuestion(state.questions, parentQuestionId, questionToAdd);
        state.questions = newQuestionSet;
      } else {
        const maxOrder = state.questions.reduce((mx, ques) => Math.max(mx, ques.order), 0);
        questionToAdd = {
          ...questionToAdd,
          order: maxOrder + 1,
        };
        state.questions.push(questionToAdd);
      }
    },

    addNewOption(state, action: PayloadAction<{ questionId: number }>) {
      const { questionId } = action.payload;
      const newOption = { textBangla: '', textEnglish: '', value: '1', id: Date.now() % 10000000 };
      const question = findQuestion(state.questions, questionId);
      if (question != null) {
        const newQuestionSet = addOption(state.questions, questionId, newOption);
        state.questions = newQuestionSet;
      }
    },

    updateForm(state, action: PayloadAction<FormDataType>) {
      const { nameBangla, nameEnglish, hash, formJson, formType, questions, status } =
        action.payload;
      state.nameBangla = nameBangla;
      state.nameEnglish = nameEnglish;
      state.hash = hash;
      state.formJson = formJson;
      state.formType = formType;
      state.status = status;
      state.questions = questions;
    },

    updateFormTitle(state, action: PayloadAction<string>) {
      state.nameBangla = action.payload;
      state.nameEnglish = action.payload;
    },

    updateQuestion(state, action: PayloadAction<QuestionUpdateArg>) {
      const { questionId, updates } = action.payload;
      state.questions = updateQuestionFields(state.questions, questionId, updates);
    },

    updateOption(state, action: PayloadAction<OptionUpdateArg>) {
      state.questions = updateOptionText(state.questions, action.payload);
    },

    removeQuestion(state, action: PayloadAction<QuestionRemoveArg>) {
      const questionSetCopy: Question[] = [...state.questions];
      const newQuestionSet = removeQuestionFromSet(questionSetCopy, action.payload.questionId);
      state.questions = newQuestionSet;
    },

    removeOption(state, action: PayloadAction<OptionRemoveArg>) {
      const questionSetCopy: Question[] = [...state.questions];

      const newQuestionSet = removeOptionFromSet(questionSetCopy, action.payload);
      state.questions = newQuestionSet;
    },
    moveQuestionUp(state, action: PayloadAction<QuestionOrderChangeArg>) {
      const { questionId, parentQuestionId } = action.payload;

      if (parentQuestionId === -1) {
        const newQuestion = changeToHigherOrder([...state.questions], questionId);
        if (newQuestion) {
          state.questions = newQuestion;
        }
      } else {
        const question = findQuestion(state.questions, parentQuestionId);
        if (question) {
          const childQuestions = question.childQuestions ?? [];
          const newQuestions = changeToHigherOrder(childQuestions, questionId);
          if (newQuestions) {
            state.questions = updateChildQuestions(state.questions, parentQuestionId, newQuestions);
          }
        }
      }
    },
    moveQuestionDown(state, action: PayloadAction<QuestionOrderChangeArg>) {
      const { questionId, parentQuestionId } = action.payload;

      if (parentQuestionId === -1) {
        const newQuestion = changeToLowerOrder([...state.questions], questionId);
        if (newQuestion) {
          state.questions = newQuestion;
        }
      } else {
        const question = findQuestion(state.questions, parentQuestionId);
        if (question) {
          const childQuestions = question.childQuestions ?? [];
          const newQuestions = changeToLowerOrder(childQuestions, questionId);
          if (newQuestions) {
            state.questions = updateChildQuestions(state.questions, parentQuestionId, newQuestions);
          }
        }
      }
    },
    resetForm: () => initialState,
  },
});

export const {
  resetForm,
  updateForm,
  addNewOption,
  updateOption,
  removeOption,
  moveQuestionUp,
  addNewQuestion,
  updateQuestion,
  removeQuestion,
  moveQuestionDown,
  updateFormTitle,
} = formSlice.actions;

export const formData = (state: RootState) => state.FormData;

export default formSlice.reducer;
